import React, { Fragment, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { UsageRow } from '@/pages/organization/components/UsageRow';
import { AIToolUsageUserList } from '@/pages/organization/components/WidgetUserLists/AIToolUsageUserList';
import { useOrgTopShadowAiTools } from '@/hooks/api/useShadowAiTools';

type AIToolUsage = {
  id: string;
  name: string;
  usage: number;
  category: string;
  addedAt: string | null;
  lastUsed: string | null;
};

export const TopAIInstalledTools: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data, isLoading, isError, refetch } = useOrgTopShadowAiTools(
    organizationId,
    { limit: 10, sourceType: 'installed' }
  );

  const [selectedTool, setSelectedTool] = React.useState<AIToolUsage | null>(
    null
  );

  // Transform data to match expected format
  const tools = useMemo(
    () =>
      data?.tools.map(tool => ({
        id: tool.toolId,
        name: tool.name,
        usage: tool.userCount,
        category: tool.category,
        addedAt: tool.addedAt,
        lastUsed: tool.lastUsed,
      })) ?? [],
    [data]
  );

  // Use the highest tool count as the total (for percentage calculations)
  const total = useMemo(() => {
    if (tools.length === 0) return 0;
    return Math.max(...tools.map(t => t.usage));
  }, [tools]);

  const handleRowClick = (tool: AIToolUsage) => {
    setSelectedTool(tool);
  };

  const handleCloseModal = () => {
    setSelectedTool(null);
  };

  return (
    <Fragment>
      <DashboardWidgetWrapper
        className="col-span-12 xl:col-span-5"
        title="Top Installed AI Tools"
        description="Shows up to 10 Installed AI Tools"
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-4 pr-2">
          {tools.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              No installed AI tools found.
            </div>
          ) : (
            tools.map((tool, index) => (
              <UsageRow
                key={`${tool.id}-${index}`}
                id={tool.id}
                name={tool.name}
                usage={tool.usage}
                total={total}
                onClick={() => handleRowClick(tool)}
              />
            ))
          )}
        </div>
      </DashboardWidgetWrapper>
      <AIToolUsageUserList
        toolName={selectedTool?.name || ''}
        toolId={selectedTool?.id || ''}
        category={selectedTool?.category}
        source="installed"
        open={selectedTool !== null}
        onClose={handleCloseModal}
      />
    </Fragment>
  );
};
